import { useFormik } from "formik";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext } from "react";
import { BlockPicker } from "react-color";
import { Scenario } from "../../../api/firebase/types/scenario.types";
import { COLORS } from "../../../components/picker/colors";
import IconSelectionButton from "../../../components/picker/iconPicker/IconSelectionButton";
import ToastContext from "../../../components/toast/ToastContext";
import ScenarioStore from "../../../store/ScenarioStore";
import { ScenarioToasts } from "../../../toastMessages/scenarioToastMessages";
import ContactContactOptionTable from "./ContactContactOptionTable";
import GroupContactOptionTable from "./GroupContactOptionTable";

export type ScenarioFormData = Omit<
  Scenario,
  "id" | "userId" | "emergencyCall"
>;

function ScenarioExpansionTemplate({ scenario }: { scenario: Scenario }) {
  const showToast = useContext(ToastContext);
  const formik = useFormik<ScenarioFormData>({
    initialValues: {
      title: scenario.title || "",
      incidentDetails: scenario.incidentDetails || "",
      contactDeviceContacts: scenario.contactDeviceContacts || false,
      contactSettings: scenario.contactSettings || new Map(),
      contactGroupSettings: scenario.contactGroupSettings || new Map(),
      color: scenario.color,
      icon: scenario.icon,
    },
    validate: (data) => {
      let errors: Partial<Record<keyof ScenarioFormData, string>> = {};

      if (!data.title) {
        errors.title = "Bitte geben Sie dem Szenario eine Bezeichnung";
      }

      return errors;
    },
    onSubmit: async (data) => {
      try {
        await ScenarioStore.updateScenario({
          ...scenario,
          ...data,
        });
        showToast(ScenarioToasts.scenarioModified);
        formik.resetForm({ values: data });
      } catch (e) {
        showToast(ScenarioToasts.scenarioModificationError);
      }
    },
  });

  const isFormFieldValid = (name: keyof ScenarioFormData) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: keyof ScenarioFormData) =>
    isFormFieldValid(name) ? (
      <small className="p-error p-d-block form-error">
        {formik.errors[name]}
      </small>
    ) : undefined;

  return (
    <form id="scenario-settings" onSubmit={formik.handleSubmit}>
      <h3 className="p-mt-0 p-mb-2">Bezeichnung</h3>
      <InputText
        id="title"
        className="p-m-2"
        value={formik.values.title}
        onChange={formik.handleChange}
        placeholder="z.B. Medizinischer Notfall"
      />
      {getFormErrorMessage("title")}
      <h3 className="p-mt-4 p-mb-2">Zusätzliche Informationen</h3>
      <InputTextarea
        id="incidentDetails"
        className="p-m-2"
        value={formik.values.incidentDetails}
        onChange={formik.handleChange}
        placeholder="z.B. Feuerlöscher befinden sich im Raum 123"
        rows={5}
        cols={50}
        autoResize
      />

      <h3 className="p-mt-4 p-mb-2">Hintergrundfarbe des Szenarios</h3>
      <p>
        <i>
          Wählen Sie hier aus, in welcher Farbe das Szenario in den Desktop
          Clients angezeigt werden soll.
        </i>
      </p>
      <BlockPicker
        className="p-m-2"
        color={formik.values.color}
        colors={COLORS}
        onChangeComplete={(color) => formik.setFieldValue("color", color.hex)}
        width="inherit"
      />

      <h3 className="p-mt-5 p-mb-2">Icon des Scenarios</h3>
      <p>
        <i>
          Wählen Sie hier ein Icon für die Anzeige des Szenarios im Desktop
          Client aus.
        </i>
      </p>
      <IconSelectionButton
        icon={formik.values.icon}
        onChange={(icon) => formik.setFieldValue("icon", icon)}
      />

      <h3 className="p-mt-4 p-mb-2">Meldeinstanz-Kontakte</h3>
      <p>
        <i>
          Wählen Sie hier aus, ob die in der Meldeinstanz hinterlegten Kontakte
          benachrichtigt werden sollen.
        </i>
      </p>
      <div className="p-m-2">
        <Checkbox
          id="contactDeviceContacts"
          className="p-mr-2"
          inputId="contactDeviceContacts"
          checked={formik.values.contactDeviceContacts}
          onChange={formik.handleChange}
        />
        <label htmlFor="contactDeviceContacts">
          Meldeinstanz-spezifische Kontakte kontaktieren
        </label>
      </div>

      <ContactContactOptionTable
        contactSettings={formik.values.contactSettings!}
        onSettingsChange={(newContactSettings) =>
          formik.setFieldValue("contactSettings", newContactSettings)
        }
        description="Wählen Sie hier aus, welche Kontakte auf welche Weise
        für dieses Szenario kontaktiert werden sollen."
      />
      <GroupContactOptionTable
        contactGroupSettings={formik.values.contactGroupSettings!}
        onSettingsChange={(newGroupSettings) =>
          formik.setFieldValue("contactGroupSettings", newGroupSettings)
        }
        description="Wählen Sie hier aus, welche Kontaktgruppen auf welche Weise
        für dieses Szenario kontaktiert werden sollen."
      />

      <div
        className="p-mt-3 p-d-flex"
        style={{ width: "100%", maxWidth: "12rem" }}
      >
        <Button
          className={`p-button-${formik.dirty ? "warning" : "primary"}`}
          key="save-button"
          type="submit"
          form="scenario-settings"
          label="Speichern"
        />
        <Button
          type="button"
          key="cancel-button"
          className="p-button-secondary p-button-text"
          label="Abbrechen"
          onClick={() => formik.resetForm()}
        />
      </div>
    </form>
  );
}

export default observer(ScenarioExpansionTemplate);
